import { CELL_COUNT, MAX_STACK_HEIGHT } from "./geometry";
import {
  freezeGameState,
  isPlayer,
  opponentOf,
  type Cell,
  type GameOutcome,
  type GameState,
  type GameStateInput,
} from "./model";
import {
  countDiscs,
  findOrphans,
  generateLegacyLegalMovesUnchecked,
  generateLegalMovesUnchecked,
  generateVersion2LegalMovesUnchecked,
  generateVersion3LegalMovesUnchecked,
} from "./rules";

export type StateValidationIssue = Readonly<{
  code:
    | "invalid-shape"
    | "invalid-cell-count"
    | "invalid-cell"
    | "invalid-stack"
    | "too-many-discs"
    | "orphan-field"
    | "invalid-outcome"
    | "missing-terminal-outcome";
  path: string;
  message: string;
}>;

export type StateValidationResult =
  | Readonly<{ ok: true; state: GameState }>
  | Readonly<{ ok: false; issues: readonly StateValidationIssue[] }>;

export class GameStateValidationError extends Error {
  readonly issues: readonly StateValidationIssue[];

  constructor(issues: readonly StateValidationIssue[]) {
    super(issues.map((issue) => `${issue.path}: ${issue.message}`).join("; "));
    this.name = "GameStateValidationError";
    this.issues = issues;
  }
}

function issue(
  code: StateValidationIssue["code"],
  path: string,
  message: string,
): StateValidationIssue {
  return Object.freeze({ code, path, message });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactlyKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(record).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function decodeCell(value: unknown, path: string, issues: StateValidationIssue[]): Cell | null {
  if (!isRecord(value) || typeof value["kind"] !== "string") {
    issues.push(issue("invalid-cell", path, "Expected a removed cell or field cell."));
    return null;
  }

  if (value["kind"] === "removed") {
    if (!hasExactlyKeys(value, ["kind"])) {
      issues.push(issue("invalid-cell", path, "Removed cells may contain only the kind property."));
      return null;
    }
    return { kind: "removed" };
  }

  const rawStack = value["stack"];
  if (
    value["kind"] !== "field" ||
    !hasExactlyKeys(value, ["kind", "stack"]) ||
    !Array.isArray(rawStack)
  ) {
    issues.push(
      issue("invalid-cell", path, "Field cells require exactly kind and stack properties."),
    );
    return null;
  }

  const decodedStack = [...(rawStack as unknown[])];
  if (decodedStack.length > MAX_STACK_HEIGHT || !decodedStack.every(isPlayer)) {
    issues.push(
      issue(
        "invalid-stack",
        `${path}.stack`,
        `A stack must contain zero to ${MAX_STACK_HEIGHT} black or red discs.`,
      ),
    );
    return null;
  }

  return { kind: "field", stack: decodedStack };
}

function decodeOutcome(
  value: unknown,
  path: string,
  issues: StateValidationIssue[],
): GameOutcome | null | undefined {
  if (value === null) return null;
  if (!isRecord(value) || typeof value["kind"] !== "string") {
    issues.push(issue("invalid-outcome", path, "Expected null or a supported terminal outcome."));
    return undefined;
  }

  if (
    value["kind"] === "passage" &&
    hasExactlyKeys(value, ["kind", "winner"]) &&
    isPlayer(value["winner"])
  ) {
    return { kind: "passage", winner: value["winner"] };
  }

  if (
    value["kind"] === "no-moves" &&
    hasExactlyKeys(value, ["kind", "winner", "loser"]) &&
    isPlayer(value["winner"]) &&
    isPlayer(value["loser"])
  ) {
    return { kind: "no-moves", winner: value["winner"], loser: value["loser"] };
  }

  issues.push(issue("invalid-outcome", path, "Outcome properties or player values are invalid."));
  return undefined;
}

type LegalMoveGenerator = typeof generateLegalMovesUnchecked;

function validateGameStateWith(
  value: unknown,
  generateMoves: LegalMoveGenerator,
): StateValidationResult {
  const issues: StateValidationIssue[] = [];
  if (!isRecord(value) || !hasExactlyKeys(value, ["cells", "activePlayer", "outcome"])) {
    return Object.freeze({
      ok: false,
      issues: Object.freeze([
        issue(
          "invalid-shape",
          "$",
          "A game state requires exactly cells, activePlayer, and outcome properties.",
        ),
      ]),
    });
  }

  if (!isPlayer(value["activePlayer"])) {
    issues.push(issue("invalid-shape", "$.activePlayer", "Expected black or red."));
  }

  const rawCells = value["cells"];
  if (!Array.isArray(rawCells) || rawCells.length !== CELL_COUNT) {
    issues.push(
      issue("invalid-cell-count", "$.cells", `Expected exactly ${CELL_COUNT} board cells.`),
    );
  }

  const cells =
    Array.isArray(rawCells) && rawCells.length === CELL_COUNT
      ? Array.from({ length: CELL_COUNT }, (_, index) =>
          decodeCell(rawCells[index], `$.cells[${index}]`, issues),
        )
      : [];
  const outcome = decodeOutcome(value["outcome"], "$.outcome", issues);

  if (
    issues.length > 0 ||
    !isPlayer(value["activePlayer"]) ||
    cells.length !== CELL_COUNT ||
    cells.some((cell) => cell === null) ||
    outcome === undefined
  ) {
    return Object.freeze({ ok: false, issues: Object.freeze(issues) });
  }

  const state = freezeGameState({
    cells: cells as readonly Cell[],
    activePlayer: value["activePlayer"],
    outcome,
  });

  for (const player of ["black", "red"] as const) {
    if (countDiscs(state, player) > 5) {
      issues.push(
        issue("too-many-discs", "$.cells", `${player} cannot have more than five discs.`),
      );
    }
  }

  const orphans = findOrphans(state.cells);
  for (const orphan of orphans) {
    issues.push(
      issue(
        "orphan-field",
        `$.cells[${orphan.position.row * 5 + orphan.position.column}]`,
        "Canonical states cannot retain a zero-neighbor field.",
      ),
    );
  }

  const stateWithoutOutcome: GameState = { ...state, outcome: null };
  const availableMoves = generateMoves(stateWithoutOutcome);
  if (state.outcome === null && availableMoves.length === 0) {
    issues.push(
      issue(
        "missing-terminal-outcome",
        "$.outcome",
        "A non-terminal active player must have at least one legal move.",
      ),
    );
  } else if (state.outcome?.kind === "passage" && state.outcome.winner !== state.activePlayer) {
    issues.push(
      issue(
        "invalid-outcome",
        "$.outcome.winner",
        "A passage winner remains the active player in the terminal post-state.",
      ),
    );
  } else if (state.outcome?.kind === "no-moves") {
    if (
      state.outcome.loser !== state.activePlayer ||
      state.outcome.winner !== opponentOf(state.outcome.loser) ||
      availableMoves.length > 0
    ) {
      issues.push(
        issue(
          "invalid-outcome",
          "$.outcome",
          "A no-moves outcome must name the immobile active player as loser and the opponent as winner.",
        ),
      );
    }
  }

  return issues.length === 0
    ? Object.freeze({ ok: true, state })
    : Object.freeze({ ok: false, issues: Object.freeze(issues) });
}

export function validateGameState(value: unknown): StateValidationResult {
  return validateGameStateWith(value, generateLegalMovesUnchecked);
}

/** Rules-version 1 validation retained for persisted match compatibility. */
export function validateLegacyGameState(value: unknown): StateValidationResult {
  return validateGameStateWith(value, generateLegacyLegalMovesUnchecked);
}

/** Rules-version 2 validation retained for persisted match compatibility. */
export function validateVersion2GameState(value: unknown): StateValidationResult {
  return validateGameStateWith(value, generateVersion2LegalMovesUnchecked);
}

/** Rules-version 3 validation retained for persisted match compatibility. */
export function validateVersion3GameState(value: unknown): StateValidationResult {
  return validateGameStateWith(value, generateVersion3LegalMovesUnchecked);
}

export function createGameState(input: GameStateInput): GameState {
  const result = validateGameState({
    cells: input.cells,
    activePlayer: input.activePlayer,
    outcome: input.outcome ?? null,
  });
  if (!result.ok) throw new GameStateValidationError(result.issues);
  return result.state;
}
